
import { useState, useRef, useEffect } from 'react';
import { cozeApiService, CozeMessage } from '../../services/cozeApi';

interface CozeChatProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CozeChat({ isOpen, onClose }: CozeChatProps) {
  const [messages, setMessages] = useState<CozeMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiKey, setApiKey] = useState('sat_DVWes8e4MfCL27Ye2s3zJHwgBo895ofUztmBnMXyr5lcbw0Hwz2lOTY187u8uNVf');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const savedApiKey = localStorage.getItem('coze_api_key');
    if (savedApiKey) {
      setApiKey(savedApiKey);
    } else {
      // 使用默认的 API 密钥
      const defaultApiKey = 'sat_DVWes8e4MfCL27Ye2s3zJHwgBo895ofUztmBnMXyr5lcbw0Hwz2lOTY187u8uNVf';
      setApiKey(defaultApiKey);
      cozeApiService.setApiKey(defaultApiKey);
      
      // 添加欢迎消息
      const welcomeMessage: CozeMessage = {
        role: 'assistant',
        content: '您好！我是 Coze AI 助手，API 密钥已配置完成，很高兴为您服务。请问有什么可以帮助您的吗？',
        timestamp: Date.now()
      };
      setMessages([welcomeMessage]);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: CozeMessage = {
      role: 'user',
      content: inputMessage.trim(),
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await cozeApiService.sendMessage(userMessage.content);
      
      if (response.success) {
        const assistantMessage: CozeMessage = {
          role: 'assistant',
          content: response.message,
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        const errorMessage: CozeMessage = {
          role: 'assistant',
          content: `错误: ${response.error}`,
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage: CozeMessage = {
        role: 'assistant',
        content: '抱歉，发送消息时出现错误，请稍后重试。',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSaveApiKey = () => {
    if (apiKey.trim()) {
      cozeApiService.setApiKey(apiKey.trim());
      setShowApiKeyInput(false);
      // 添加欢迎消息
      const welcomeMessage: CozeMessage = {
        role: 'assistant',
        content: '您好！我是 Coze AI 助手，API 密钥已更新成功，很高兴为您服务。请问有什么可以帮助您的吗？',
        timestamp: Date.now()
      };
      setMessages([welcomeMessage]);
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl h-[600px] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <i className="ri-robot-line text-white text-lg"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Coze AI 助手</h3>
              <p className="text-sm text-gray-500">智能对话服务</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={clearChat}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              title="清空对话"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
            <button
              onClick={() => setShowApiKeyInput(!showApiKeyInput)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              title="API设置"
            >
              <i className="ri-settings-line"></i>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>

        {/* API密钥配置 */}
        {showApiKeyInput && (
          <div className="p-4 bg-yellow-50 border-b border-yellow-200">
            <div className="flex items-center space-x-2 mb-2">
              <i className="ri-key-line text-yellow-600"></i>
              <span className="text-sm font-medium text-yellow-800">配置 Coze API 密钥</span>
            </div>
            <div className="flex space-x-2">
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="请输入您的 Coze API 密钥"
                className="flex-1 px-3 py-2 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
              />
              <button
                onClick={handleSaveApiKey}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm cursor-pointer whitespace-nowrap"
              >
                保存
              </button>
            </div>
          </div>
        )}

        {/* 消息区域 */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && !showApiKeyInput && (
            <div className="text-center text-gray-500 mt-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-chat-3-line text-2xl text-gray-400"></i>
              </div>
              <p>开始与 Coze AI 助手对话吧！</p>
            </div>
          )}

          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                <p className={`text-xs mt-1 ${
                  message.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {new Date(message.timestamp).toLocaleTimeString('zh-CN', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg px-4 py-2">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  <span className="text-sm text-gray-600">AI 正在思考...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* 输入区域 */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex space-x-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="输入您的消息..."
              disabled={isLoading || showApiKeyInput}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <button
              onClick={handleSendMessage}
              disabled={isLoading || !inputMessage.trim() || showApiKeyInput}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              {isLoading ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-send-plane-line"></i>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
